import { CommonInput } from "../common";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useMemo } from "react";
import { i18nCrossUtil, decimalPointNoMoreX, numberToThousands, IS_WEB } from "@adgm/libs";
import { View, Text, StyleSheet } from "../platformComponents";

interface FiatWithdrawalFormProps {
  currency: string;
  currencyBalance: number;
  onValidationChange: (isValid: boolean) => void;
}

export interface FiatWithdrawalFormRef {
  getValidatedData: () => Record<string, string>;
}

const FiatWithdrawalForm = forwardRef<FiatWithdrawalFormRef, FiatWithdrawalFormProps>(
  (props, ref) => {
    const intl = i18nCrossUtil.t();
    const { currency, currencyBalance } = props;
    const [withdrawalFormData, setWithdrawalFormData] = useState({
      withdrawalAmount: "",
      bankName: "",
      swiftBicCode: "",
      bankCode: "",
      bankBranchCode: "",
      bankAddress: "",
      bankAccountName: "",
      bankAccountNumber: "",
    });

    useEffect(() => {
      const isValid = validateWithdrawalForm();
      props.onValidationChange(isValid);
    }, [withdrawalFormData]);

    const handleFieldChange = useCallback((fieldName: string, fieldValue: string | number) => {
      setWithdrawalFormData(prevData => ({
        ...prevData,
        [fieldName]: fieldValue,
      }));
    }, []);
    const withdrawalAmountDisplayError = useMemo(() => {
      if (withdrawalFormData.withdrawalAmount !== "") {
        if (Number(withdrawalFormData.withdrawalAmount) === 0) {
          return intl["input.error.amount.zero"];
        } else if (Number(withdrawalFormData.withdrawalAmount) > currencyBalance) {
          return intl["input.error.balance.insufficient"];
        }
      }
      return "";
    }, [withdrawalFormData.withdrawalAmount, currencyBalance]);

    /**
     * 表单校验逻辑
     * 1. 提币金额 > 0
     * 2. 提币金额 <= 可用余额
     * 3. 所有表单字段都不能为空
     */
    const validateWithdrawalForm = () => {
      const fields = [
        "withdrawalAmount",
        "bankName",
        "swiftBicCode",
        "bankCode",
        "bankBranchCode",
        "bankAddress",
        "bankAccountName",
        "bankAccountNumber",
      ];
      return (
        Number(withdrawalFormData.withdrawalAmount) > 0 &&
        Number(withdrawalFormData.withdrawalAmount) <= currencyBalance &&
        fields.every(field => withdrawalFormData[field] !== "")
      );
    };

    useImperativeHandle(ref, () => ({
      getValidatedData: () => {
        return withdrawalFormData;
      },
    }));

    return (
      <View
        style={{
          marginVertical: 16,
        }}>
        {/* Withdrawal Amount */}
        <View
          style={{
            flexDirection: IS_WEB ? "row" : "column",
            alignItems: "center",
            justifyContent: "space-between",
            gap: IS_WEB ? 48 : 16,
          }}>
          <View style={{ flex: 1, width: "100%" }}>
            <Text style={styles.labelText}>{intl["wallet.withdraw.fiat.amount.label"]}</Text>
            <View
              style={{
                position: "relative",
              }}>
              <CommonInput
                testID="withdrawalAmountInput"
                value={withdrawalFormData.withdrawalAmount}
                valueType="number"
                precision={2}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                onValueChange={(value: string) => handleFieldChange("withdrawalAmount", value)}
              />
              {IS_WEB ? null : (
                <Text
                  style={{
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: "700",
                    lineHeight: 16,
                    position: "absolute",
                    right: 0,
                    top: 16,
                  }}>
                  {currency}
                </Text>
              )}
            </View>
            {withdrawalAmountDisplayError ? (
              <Text style={styles.errorText}>{withdrawalAmountDisplayError}</Text>
            ) : null}
          </View>
          <View style={{ flex: 1, width: "100%" }}>
            <Text style={styles.labelText}>{intl["wallet.withdraw.fiat.available.amount"]}</Text>
            <Text
              style={{
                color: "#fff",
                fontSize: 24,
                fontWeight: "700",
                lineHeight: 26,
                marginTop: 8,
              }}>
              {numberToThousands(decimalPointNoMoreX(currencyBalance, 2))} {IS_WEB ? currency : ""}
            </Text>
          </View>
        </View>

        {/* Bank Info */}
        <>
          <Text style={styles.titleText}>{intl["wallet.withdraw.fiat.bank.info"]}</Text>
          <View
            style={{
              flexDirection: IS_WEB ? "row" : "column",
              alignItems: "center",
              justifyContent: "space-between",
              gap: IS_WEB ? 48 : 16,
            }}>
            <View style={{ flex: 1, width: "100%" }}>
              <Text testID="bankNameLabel" style={styles.labelText}>
                {intl["wallet.withdraw.fiat.bank.name.label"]}
              </Text>
              <CommonInput
                testID="bankNameInput"
                value={withdrawalFormData.bankName}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("bankName", value)}
              />
            </View>
            <View style={{ flex: 1, width: "100%" }}>
              <Text testID="swiftBicCodeLabel" style={styles.labelText}>
                {intl["wallet.withdraw.fiat.swift.bic.code.label"]}
              </Text>
              <CommonInput
                testID="swiftBicCodeInput"
                value={withdrawalFormData.swiftBicCode}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("swiftBicCode", value)}
              />
            </View>
          </View>

          <View
            style={{
              flexDirection: IS_WEB ? "row" : "column",
              alignItems: "center",
              justifyContent: "space-between",
              gap: IS_WEB ? 48 : 16,
            }}>
            <View style={{ flex: 1, width: "100%" }}>
              <Text style={styles.labelText}>{intl["wallet.withdraw.fiat.bank.code.label"]}</Text>
              <CommonInput
                testID="bankCodeInput"
                value={withdrawalFormData.bankCode}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("bankCode", value)}
              />
            </View>
            <View style={{ flex: 1, width: "100%" }}>
              <Text style={styles.labelText}>{intl["wallet.withdraw.fiat.branch.code.label"]}</Text>
              <CommonInput
                testID="branchCodeInput"
                value={withdrawalFormData.bankBranchCode}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("bankBranchCode", value)}
              />
            </View>
          </View>

          <View>
            <Text style={styles.labelText}>{intl["wallet.withdraw.fiat.bank.address.label"]}</Text>
            <CommonInput
              testID="bankAddressInput"
              value={withdrawalFormData.bankAddress}
              placeholder={intl["input.placeholder"]}
              placeholderTextColor="#5A5A5A"
              style={styles.inputField}
              maxLength={500}
              onChangeText={(value: string) => handleFieldChange("bankAddress", value)}
            />
          </View>
        </>

        {/* Account Info */}
        <>
          <Text style={styles.titleText}>{intl["wallet.withdraw.fiat.account.info"]}</Text>
          <View
            style={{
              flexDirection: IS_WEB ? "row" : "column",
              alignItems: "center",
              justifyContent: "space-between",
              gap: IS_WEB ? 48 : 16,
              marginBottom: 16,
            }}>
            <View style={{ flex: 1, width: "100%" }}>
              <Text style={styles.labelText}>
                {intl["wallet.withdraw.fiat.account.holder.name.label"]}
              </Text>
              <CommonInput
                testID="bankAccountNameInput"
                value={withdrawalFormData.bankAccountName}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("bankAccountName", value)}
              />
            </View>
            <View style={{ flex: 1, width: "100%" }}>
              <Text style={styles.labelText}>
                {intl["wallet.withdraw.fiat.bank.account.number.label"]}
              </Text>
              <CommonInput
                testID="bankAccountNumberInput"
                value={withdrawalFormData.bankAccountNumber}
                placeholder={intl["input.placeholder"]}
                placeholderTextColor="#5A5A5A"
                style={styles.inputField}
                maxLength={200}
                onChangeText={(value: string) => handleFieldChange("bankAccountNumber", value)}
              />
            </View>
          </View>
        </>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  titleText: IS_WEB
    ? {
        color: "#fff",
        fontSize: 16,
        lineHeight: 24,
        fontWeight: "500",
        marginBottom: 8,
        marginTop: 16,
      }
    : {
        color: "#9E9E9E",
        fontSize: 14,
        fontWeight: "400",
        marginBottom: 8,
        marginTop: 16,
      },
  labelText: IS_WEB
    ? {
        color: "rgba(255, 255, 255, 0.70)",
        fontSize: 14,
        fontWeight: "400",
        lineHeight: 22,
        marginTop: 16,
      }
    : {
        color: "#D9D9D9",
        fontSize: 13,
        fontWeight: "500",
        lineHeight: 16,
        marginTop: 16,
      },
  inputField: IS_WEB
    ? {
        color: "rgba(255, 255, 255, 0.70)",
        borderWidth: 0,
        borderColor: "transparent",
        backgroundColor: "#2F3342",
        fontSize: 14,
        lineHeight: 22,
        paddingVertical: 9,
        paddingHorizontal: 16,
        marginBottom: 4,
        borderRadius: 2,
        marginTop: 8,
      }
    : {
        color: "#fff",
        fontSize: 14,
        lineHeight: 16,
        borderBottomWidth: 1,
        borderColor: "#202020",
        paddingVertical: 16,
        marginBottom: 4,
      },
  errorText: {
    color: "#E23030",
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 12,
  },
});

export { FiatWithdrawalForm };
